// ViewContext — controls whether the sidebar/topbar/dashboards are in
// "Priority Risk Internal" mode or a "Client Portal" mode (branded for one client).
// Internal users (director, administrator, consultant) can switch.
// Portal users are locked to their own client.
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "./AuthContext";

const ViewContext = createContext(null);
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export function ViewProvider({ children }) {
    const { user } = useAuth();
    const [clients, setClients] = useState([]);
    const [portalClientId, setPortalClientId] = useState(() => {
        try { return sessionStorage.getItem("pr_view_as_client") || null; } catch { return null; }
    }); // null = internal view

    useEffect(() => {
        if (!user) return;
        const portalOnly = !INTERNAL_ROLES.has(user.role);
        const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        // Skip the cross-tenant /clients fetch for portal-only roles — they have no
        // permission on that endpoint and the view-switcher is locked for them anyway.
        if (!portalOnly) load();
        if (portalOnly && user.client_id) {
            setPortalClientId(user.client_id);
            // For portal users we still want at least their own client metadata for the
            // sidebar logo + header band — fetch the single client they're scoped to.
            const loadOwn = () => api.get(`/clients/${user.client_id}`)
                .then((r) => setClients([r.data]))
                .catch(() => {});
            loadOwn();
            window.addEventListener("clients:changed", loadOwn);
            return () => window.removeEventListener("clients:changed", loadOwn);
        }
        if (!portalOnly) window.addEventListener("clients:changed", load);
        return () => {
            if (!portalOnly) window.removeEventListener("clients:changed", load);
        };
    }, [user]);

    const portalClient = useMemo(
        () => clients.find((c) => c.id === portalClientId) || null,
        [clients, portalClientId]
    );

    // For portal-only roles isInternalView is ALWAYS false — independent of whether
    // the client metadata has loaded yet (prevents a flash of the internal sidebar).
    // For internal roles it depends on whether they've switched into a portal view.
    const portalOnly = user && !INTERNAL_ROLES.has(user.role);
    const isInternalView = portalOnly ? false : !portalClientId;
    const canSwitch = user && INTERNAL_ROLES.has(user.role);

    function enterPortal(clientId) {
        setPortalClientId(clientId);
        try { sessionStorage.setItem("pr_view_as_client", clientId); } catch { /* ignore */ }
    }
    function exitPortal() {
        if (canSwitch) {
            setPortalClientId(null);
            try { sessionStorage.removeItem("pr_view_as_client"); } catch { /* ignore */ }
        }
    }

    return (
        <ViewContext.Provider value={{
            clients, portalClient, portalClientId,
            isInternalView, canSwitch, enterPortal, exitPortal,
        }}>
            {children}
        </ViewContext.Provider>
    );
}

export function useView() {
    const ctx = useContext(ViewContext);
    if (!ctx) throw new Error("useView must be inside ViewProvider");
    return ctx;
}
